import React, { useContext, useEffect, useMemo, useState } from 'react';
import { MessageSquare, Plus, Loader2 } from 'lucide-react';
import Select from 'react-select';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useContextHook } from 'use-context-hook';
import { AuthContext } from '../../Context/authContext';
import { TemporaryChatContext } from '../../Context/temporaryChatContext';
import sessionsService from '../../services/sessionsServices';
import agentService from '../../services/agentService';
import teamService from '../../services/teamService';
import Toast from '../Toast';
import AccentButton from '../ui/AccentButton';
import {
  DEFAULT_WALT_ID,
  DEFAULT_WALT_AGENT,
  LEGACY_WALT_IDS,
} from './waltDefaults';

export { DEFAULT_WALT_ID, DEFAULT_WALT_AGENT } from './waltDefaults';

function toAgentOption(agent) {
  return {
    label: agent.agent_name,
    value: agent.id ?? agent.agent_id,
    icon: agent.icon,
    mode: 'agent',
  };
}

function toTeamOption(team) {
  return {
    label: team.team_name,
    value: team.team_id ?? team.id,
    icon: team.icon,
    mode: 'team',
  };
}

function isTeamSelection(agent) {
  return agent?.mode === 'team';
}

function findAgentById(agents, agentId) {
  if (!agents?.length || agentId == null || agentId === '') return null;
  return agents.find(a => String(a.id ?? a.agent_id) === String(agentId)) || null;
}

function findWaltAgent(agents) {
  if (!agents?.length) return null;
  return (
    agents.find(a => (a.agent_name || '').toLowerCase() === 'walt') ||
    agents.find(a => LEGACY_WALT_IDS.has(String(a.id ?? a.agent_id))) ||
    null
  );
}

function isWaltSelection(agent) {
  if (!agent) return false;
  return LEGACY_WALT_IDS.has(String(agent.value)) || (agent.label || '').toLowerCase() === 'walt';
}

function flattenAgentOptions(groupedOptions) {
  return groupedOptions.flatMap(group => group.options || []);
}

function getAgentIconSrc(agent) {
  if (!agent?.icon) return null;
  if (agent.icon.startsWith('http://') || agent.icon.startsWith('https://')) {
    return agent.icon.split('?')[0].toLowerCase().endsWith('.svg') ? null : agent.icon;
  }
  if (agent.icon.startsWith('data:')) return agent.icon;
  return `data:image/png;base64,${agent.icon}`;
}

const agentSelectStyles = {
  control: (base, state) => ({
    ...base,
    minHeight: 34,
    minWidth: 180,
    backgroundColor: '#fff',
    borderColor: state.isFocused ? 'rgba(114, 27, 31, 0.4)' : '#e5e5e5',
    borderRadius: 8,
    boxShadow: 'none',
    fontSize: 14,
    cursor: state.isDisabled ? 'not-allowed' : 'pointer',
    opacity: state.isDisabled ? 0.6 : 1,
    '&:hover': { borderColor: state.isFocused ? 'rgba(114, 27, 31, 0.4)' : '#d4d4d4' },
  }),
  valueContainer: base => ({ ...base, padding: '0 8px' }),
  input: base => ({ ...base, color: 'var(--text-color)', margin: 0, padding: 0 }),
  singleValue: base => ({ ...base, color: 'var(--text-color)', margin: 0 }),
  placeholder: base => ({ ...base, color: '#a0a0a0', margin: 0 }),
  indicatorSeparator: () => ({ display: 'none' }),
  dropdownIndicator: base => ({
    ...base,
    color: '#6d6d6d',
    padding: '0 8px',
    '&:hover': { color: 'var(--text-color)' },
  }),
  menu: base => ({
    ...base,
    backgroundColor: '#fff',
    border: '1px solid #e5e5e5',
    borderRadius: 8,
    overflow: 'hidden',
    zIndex: 50,
    boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
  }),
  menuList: base => ({ ...base, padding: 4, maxHeight: 280 }),
  group: base => ({ ...base, paddingTop: 4, paddingBottom: 4 }),
  groupHeading: base => ({
    ...base,
    color: '#6d6d6d',
    fontSize: 11,
    fontWeight: 600,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    margin: 0,
    padding: '6px 10px 4px',
  }),
  option: (base, state) => ({
    ...base,
    backgroundColor: state.isSelected ? 'rgba(114, 27, 31, 0.08)' : state.isFocused ? '#f7f6f2' : 'transparent',
    color: 'var(--text-color)',
    fontSize: 14,
    cursor: 'pointer',
    borderRadius: 6,
    padding: '8px 10px',
  }),
  noOptionsMessage: base => ({ ...base, color: '#6d6d6d', fontSize: 13 }),
};

export default function WaltChatHeader() {
  const navigate = useNavigate();
  const { id: routeSessionId } = useParams();
  const queryClient = useQueryClient();
  const [switching, setSwitching] = useState(false);
  const { user } = useContextHook(AuthContext, v => ({
    user: v.user,
  }));
  const { sessionAgent, selectedAgent, updateAgent, setChatmessages, setSessionAgent, setSessionID } =
    useContext(TemporaryChatContext);

  // Query key v2 busts the old domain-filtered cache that only returned "test".
  const agentsQuery = useQuery({
    queryKey: ['get-all-agents', 'v2-no-domain'],
    queryFn: () => agentService.getAllAgent(),
    staleTime: 5 * 60 * 1000,
  });
  const feedaQuery = useQuery({
    queryKey: ['get-feeda-agents', 'v2-no-domain'],
    queryFn: () => agentService.getAllfeedaAgents(),
    staleTime: 5 * 60 * 1000,
  });
  const teamsQuery = useQuery({
    queryKey: ['get-all-teams', 'chat-header'],
    queryFn: () => teamService.getAllTeam(),
    staleTime: 5 * 60 * 1000,
  });

  const userAgents = useMemo(() => agentsQuery.data?.agents || [], [agentsQuery.data?.agents]);
  const adminAgents = useMemo(() => feedaQuery.data?.agents || [], [feedaQuery.data?.agents]);
  const userTeams = useMemo(() => teamsQuery.data?.teams || [], [teamsQuery.data?.teams]);
  const allAgents = useMemo(() => [...adminAgents, ...userAgents], [adminAgents, userAgents]);
  const agentsReady = agentsQuery.isFetched && feedaQuery.isFetched;
  const agentsLoadingAny = agentsQuery.isLoading || feedaQuery.isLoading || teamsQuery.isLoading;

  const agentOptions = useMemo(() => {
    // Walt lives on user_created list — prefer that, then admin, then fallback id.
    const walt =
      findWaltAgent(userAgents) ||
      findAgentById(userAgents, DEFAULT_WALT_ID) ||
      findWaltAgent(adminAgents) ||
      findAgentById(adminAgents, DEFAULT_WALT_ID);

    let userOptions = userAgents.map(toAgentOption);
    if (walt) {
      const waltOption = toAgentOption(walt);
      userOptions = [
        waltOption,
        ...userOptions.filter(option => String(option.value) !== String(waltOption.value)),
      ];
    }

    const userIds = new Set(userOptions.map(option => String(option.value)));
    const adminOptions = adminAgents
      .map(toAgentOption)
      .filter(option => !userIds.has(String(option.value)));

    const teamOptions = userTeams.map(toTeamOption).filter(option => option.value != null && option.value !== '');

    const groups = [];
    if (userOptions.length) groups.push({ label: 'My agents', options: userOptions });
    if (adminOptions.length) groups.push({ label: 'Official', options: adminOptions });
    if (teamOptions.length) groups.push({ label: 'Teams', options: teamOptions });
    return groups;
  }, [adminAgents, userAgents, userTeams]);

  const flatAgentOptions = useMemo(() => flattenAgentOptions(agentOptions), [agentOptions]);

  // Match platformtl PolyChatHeader:
  // - Fresh /chat only defaults to Walt when selection is empty / Walt / leftover "test"
  // - Never overwrite a deliberately selected agent or an active team chat
  // - On an existing session, only hydrate the current agent's icon/label from API
  useEffect(() => {
    if (!agentsReady || !allAgents.length) return;
    if (isTeamSelection(selectedAgent) || isTeamSelection(sessionAgent)) return;

    const currentId = selectedAgent?.value || sessionAgent?.value;
    const freshChat = !routeSessionId;
    const leftoverTest =
      (selectedAgent?.label || '').toLowerCase() === 'test' ||
      (sessionAgent?.label || '').toLowerCase() === 'test';

    const shouldDefaultToWalt =
      freshChat &&
      (isWaltSelection(selectedAgent) || isWaltSelection(sessionAgent) || !currentId || leftoverTest);

    const fromApi = shouldDefaultToWalt
      ? findWaltAgent(userAgents) ||
        findWaltAgent(allAgents) ||
        findAgentById(allAgents, DEFAULT_WALT_ID) ||
        findAgentById(allAgents, currentId)
      : findAgentById(allAgents, currentId);

    if (!fromApi) {
      // Keep explicit DEFAULT_WALT_AGENT only when we still have nothing selected.
      if (shouldDefaultToWalt && !currentId) {
        updateAgent(DEFAULT_WALT_AGENT);
        setSessionAgent(DEFAULT_WALT_AGENT);
      }
      return;
    }

    const agentObj = toAgentOption(fromApi);
    const selectedNeedsUpdate =
      String(selectedAgent?.value) !== String(agentObj.value) ||
      selectedAgent?.icon !== agentObj.icon ||
      selectedAgent?.label !== agentObj.label;

    if (selectedNeedsUpdate && (shouldDefaultToWalt || String(selectedAgent?.value) === String(agentObj.value))) {
      updateAgent(agentObj);
    }

    const sessionMatchesCurrent =
      !routeSessionId ||
      String(sessionAgent?.value) === String(currentId) ||
      isWaltSelection(sessionAgent);

    if (
      sessionMatchesCurrent &&
      (String(sessionAgent?.value) !== String(agentObj.value) ||
        sessionAgent?.icon !== agentObj.icon ||
        sessionAgent?.label !== agentObj.label)
    ) {
      if (shouldDefaultToWalt || String(sessionAgent?.value) === String(agentObj.value) || isWaltSelection(sessionAgent)) {
        setSessionAgent(agentObj);
      }
    }
  }, [
    allAgents,
    agentsReady,
    routeSessionId,
    selectedAgent,
    sessionAgent,
    updateAgent,
    setSessionAgent,
    userAgents,
  ]);

  // Keep team label/icon fresh from teams list when chatting with a team.
  useEffect(() => {
    if (!userTeams.length) return;
    const current = isTeamSelection(selectedAgent)
      ? selectedAgent
      : isTeamSelection(sessionAgent)
        ? sessionAgent
        : null;
    if (!current?.value) return;

    const fromApi = userTeams.find(t => String(t.team_id ?? t.id) === String(current.value));
    if (!fromApi) return;

    const teamObj = toTeamOption(fromApi);
    if (
      String(selectedAgent?.value) === String(teamObj.value) &&
      (selectedAgent?.label !== teamObj.label || selectedAgent?.icon !== teamObj.icon || selectedAgent?.mode !== 'team')
    ) {
      updateAgent(teamObj);
    }
    if (
      String(sessionAgent?.value) === String(teamObj.value) &&
      (sessionAgent?.label !== teamObj.label || sessionAgent?.icon !== teamObj.icon || sessionAgent?.mode !== 'team')
    ) {
      setSessionAgent(teamObj);
    }
  }, [userTeams, selectedAgent, sessionAgent, updateAgent, setSessionAgent]);

  const selectedOption = useMemo(() => {
    const current = isTeamSelection(selectedAgent)
      ? selectedAgent
      : isTeamSelection(sessionAgent)
        ? sessionAgent
        : routeSessionId && sessionAgent?.value
          ? sessionAgent
          : selectedAgent?.value
            ? selectedAgent
            : sessionAgent;
    const currentValue = current?.value;
    if (currentValue == null || currentValue === '') return null;

    const fromList = flatAgentOptions.find(
      option =>
        String(option.value) === String(currentValue) && (option.mode || 'agent') === (current?.mode || 'agent'),
    );
    if (fromList) return fromList;

    if (current?.label) {
      return {
        label: current.label,
        value: current.value,
        icon: current.icon,
        mode: current.mode || 'agent',
      };
    }
    return null;
  }, [flatAgentOptions, selectedAgent, sessionAgent, routeSessionId]);

  const displayAgent = useMemo(() => {
    if (isTeamSelection(selectedAgent)) return selectedAgent;
    if (isTeamSelection(sessionAgent)) return sessionAgent;
    if (selectedOption) {
      return {
        label: selectedOption.label,
        value: selectedOption.value,
        icon: selectedOption.icon,
        mode: selectedOption.mode || 'agent',
      };
    }
    return selectedAgent?.label ? selectedAgent : sessionAgent;
  }, [selectedOption, sessionAgent, selectedAgent]);

  const iconSrc = useMemo(() => getAgentIconSrc(displayAgent), [displayAgent]);
  const isTeamChat = isTeamSelection(displayAgent);
  const currentAgentId = selectedAgent?.value || sessionAgent?.value;
  const apiAgent =
    findAgentById(allAgents, currentAgentId) ||
    ((isWaltSelection(selectedAgent) || isWaltSelection(sessionAgent)) && findWaltAgent(allAgents));
  const waitingForAgentData = !isTeamChat && (!agentsReady || (!!apiAgent && !iconSrc && !displayAgent?.icon));

  const startSessionWith = async agentObj => {
    setSwitching(true);
    try {
      setChatmessages([]);
      updateAgent(agentObj);
      setSessionAgent(agentObj);
      const sessionRes = await sessionsService.createSession({
        user_id: user?.data?.ID,
        platform_id: 'chronolabs',
        agent_id: agentObj?.value,
        project_id: 'test',
        mode: agentObj?.mode,
      });
      setSessionID(sessionRes?.session_id);
      queryClient.invalidateQueries({ queryKey: ['sidebar_sessions'] });
      navigate(`/chat/${sessionRes?.session_id}`);
    } catch (error) {
      console.log(error);
      Toast({ type: 'error', message: 'Could not start a chat with this agent' });
    } finally {
      setSwitching(false);
    }
  };

  const handleNewChat = () => {
    const agent = selectedAgent?.value ? selectedAgent : sessionAgent;
    if (!agent?.value) {
      Toast({ type: 'info', message: 'Please select an agent first' });
      return;
    }
    startSessionWith(agent);
  };

  const handleAgentChange = option => {
    if (!option) return;
    // Selecting an agent only updates the active agent — detach any existing session so
    // chat cannot reuse an old session_id until the user creates a new one (platformtl).
    const agentObj = {
      label: option.label,
      value: option.value,
      icon: option.icon,
      mode: option.mode || 'agent',
    };
    updateAgent(agentObj);
    setSessionAgent(agentObj);
    setChatmessages([]);
    setSessionID(null);
    navigate('/chat');
  };

  return (
    <div className="poly-chat-screen-header px-6 py-3 border-b border-shell-border flex items-center gap-3 flex-shrink-0 bg-white">
      <div className="w-9 h-9 rounded-lg flex items-center justify-center flex-shrink-0 overflow-hidden bg-primary/10">
        {waitingForAgentData ? (
          <div className="w-full h-full animate-pulse bg-black/[0.06]" aria-hidden="true" />
        ) : iconSrc ? (
          <img src={iconSrc} alt="" className="w-full h-full object-cover" />
        ) : (
          <MessageSquare className="w-4 h-4 text-primary" />
        )}
      </div>
      <div className="flex-1 min-w-0">
        {waitingForAgentData ? (
          <div className="space-y-1.5" aria-hidden="true">
            <div className="h-3.5 w-20 rounded bg-black/[0.06] animate-pulse" />
            <div className="h-3 w-16 rounded bg-black/[0.06] animate-pulse" />
          </div>
        ) : (
          <>
            <p className="text-sm font-semibold text-[var(--text-color)] truncate">
              {displayAgent?.label || 'Walt'}
            </p>
            <p className="text-xs text-muted-dim">{isTeamChat ? 'Team chat' : 'Walt Chat'}</p>
          </>
        )}
      </div>
      <div className="flex items-center gap-2 flex-shrink-0">
        <AccentButton
          type="button"
          onClick={handleNewChat}
          disabled={switching || waitingForAgentData}
          className="h-[34px] px-3 py-1.5 text-sm">
          {switching ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          New Chat
        </AccentButton>
        {agentsLoadingAny && !agentsReady && !isTeamChat ? (
          <div
            className="h-[34px] min-w-[180px] rounded-lg border border-shell-card-border bg-black/[0.04] animate-pulse"
            aria-hidden="true"
          />
        ) : (
          <Select
            options={agentOptions}
            value={selectedOption}
            onChange={handleAgentChange}
            isDisabled={switching}
            isSearchable
            placeholder={isTeamChat ? 'Team selected' : 'Assign agent'}
            noOptionsMessage={() => 'No agents or teams found'}
            styles={agentSelectStyles}
            menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
            menuPosition="fixed"
            classNamePrefix="agent-select"
          />
        )}
      </div>
    </div>
  );
}
